import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import NoSSR from '../../../context/ssr/no-ssr';
import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { legendItems } from '../../../utils/competition-legends';
import { isNonLinearCompetition } from '../../../utils/match-status';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import ErrorComponent from '../../atoms/error/error';
import {
  GLIDER_ITEM_STATE_DISABLED,
  GLIDER_ITEM_STATE_ENABLED,
  GLIDER_ITEM_STATE_FOCUSED,
  IGliderItem,
} from '../../atoms/glider-item/glider-item';
import { IIcon } from '../../atoms/icons/icon';
import IconArrowLeft from '../../atoms/icons/icon-arrow-left';
import IconArrowRight from '../../atoms/icons/icon-arrow-right';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import Legend from '../../molecules/legend/legend';
import { GliderWrapper, IMenuGliderItems, MenuGliderItems } from '../../organisms';
import ResultsTable, { IResultsTable } from '../../organisms/results-table/results-table';
import ErrorBoundary from '../error-boundary/error-boundary';

export interface ICompetitionCalendarResultsTable extends IResultsTable {
  round: number;
  competitionPhase?: string | null;
  competitionPhaseRound?: string | null;
}

export interface ICompetitionCalendarResults extends IComponent {
  competitionMatchList: Array<ICompetitionCalendarResultsTable>;
  competitionMatchListMenu?: IMatchMenu;
}

interface IMatchMenu {
  gliderItems: Array<IGliderItem>;
  activeIndex: number;
}

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidCompetitionMatchList = (matchList: Array<ICompetitionCalendarResultsTable>) =>
  Boolean(matchList && matchList.length);

const createId = (i: number): string => {
  return `${t('href.anchor.prefix.id')}-${i}`;
};

const getResultTableTitle = (resultTableModel: ICompetitionCalendarResultsTable): string =>
  resultTableModel && resultTableModel.competitionPhase
    ? `${resultTableModel.competitionPhase}`
    : `${t('competition.calendar.results.table.title')} ${resultTableModel.round}`;

const classNames = (): IClassNames =>
  makeClassNames({
    gliderWrapper: 'competition-calendar-results--wrapper',
    competitionCalendarResults: 'competition-calendar-results',
  });

const iconLeft = (): React.ReactElement<IIcon> => <IconArrowLeft size="xxxsmall" color="blue" />;
const iconRight = (): React.ReactElement<IIcon> => <IconArrowRight size="xxxsmall" color="blue" />;

const ssrPlaceholder = (props: IMenuGliderItems, cn: IClassNames) => {
  return (
    <div className={cn.gliderWrapper}>
      <MenuGliderItems {...props} isSSR />
    </div>
  );
};

/**
 * COMPETITION CALENDAR COMPONENT
 */
const CompetitionCalendarResults: React.FC<ICompetitionCalendarResults> = ({
  competitionMatchList,
  competitionMatchListMenu,
  trackingData,
}) => {
  const cn = classNames();
  const firstIndexActive = competitionMatchListMenu?.activeIndex;

  const [activeIndex, setActiveIndex] = useState(firstIndexActive);

  const onIntersect = debounce(
    (item: HTMLElement) => {
      if (typeof firstIndexActive === 'number') {
        setActiveIndex(Number(item.dataset.index) + firstIndexActive);
      }
    },
    500,
    { leading: true }
  );

  useIntersect('[data-intersect="true"]', onIntersect);

  /** Effect to change the activeIndex from new competitionMatchListMenu model */
  useEffect(() => {
    setActiveIndex(competitionMatchListMenu?.activeIndex);
  }, [competitionMatchListMenu?.activeIndex]);

  /** Render LEGEND only after COMPONENT is rendered */
  const [showLegend, setShowLegend] = useState(false);
  useEffect(() => {
    setShowLegend(isNonLinearCompetition(competitionMatchList));
  }, [competitionMatchList]);

  const gliderComponentProps: IMenuGliderItems = {
    items: competitionMatchListMenu?.gliderItems,
    activeSlideIndex: activeIndex,
    variant: 'mgv',
    onItemSelectedCallback: setActiveIndex,
  };

  const missingRounds = useMemo(() => {
    const roundSequence = competitionMatchList.map((match) => match.round);
    const missingRounds = [];

    for (let i = 1; i <= Math.max(...roundSequence); i++) {
      if (roundSequence.indexOf(i) === -1) {
        missingRounds.push(i);
      }
    }
    return missingRounds;
  }, [competitionMatchList]);

  return (
    <div className={cn.competitionCalendarResults}>
      <ErrorBoundary hasError={!isValidCompetitionMatchList(competitionMatchList)} errorComponent={getErrorComponent()}>
        <React.Fragment>
          {competitionMatchListMenu && (
            <NoSSR placeholder={ssrPlaceholder(gliderComponentProps, cn)}>
              <GliderWrapper
                propsComponent={gliderComponentProps}
                Component={MenuGliderItems}
                itemWidth={66}
                duration={2}
                bordered="top-bottom"
                iconLeft={iconLeft()}
                iconRight={iconRight()}
                hasArrows
                scrollLock
                exactWidth
                topOffset
              />
            </NoSSR>
          )}
          <Spacer size={30} />
          {competitionMatchList.map((resultTableModel: ICompetitionCalendarResultsTable, index: number) => {
            const resultTableTitle = getResultTableTitle(resultTableModel);
            const id = createId(resultTableModel.round);

            return (
              <React.Fragment key={id}>
                {missingRounds.includes(resultTableModel.round - 1) && (
                  <div id={createId(resultTableModel.round - 1)} data-intersect="false" data-index={index} />
                )}
                <div id={id} data-intersect="true">
                  {resultTableModel && resultTableModel.round && (
                    <ResultsTitle title={resultTableTitle} selfUpdate={index === 0} />
                  )}
                  <ResultsTable
                    type="results-thick"
                    matchDayList={resultTableModel.matchDayList}
                    trackingData={trackingData}
                    showMatchRowLiveResult={false}
                  />
                </div>
              </React.Fragment>
            );
          })}
          {showLegend && <Legend items={legendItems} />}
        </React.Fragment>
      </ErrorBoundary>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CompetitionCalendarResults.propTypes = {
    competitionMatchList: PropTypes.arrayOf(
      PropTypes.shape({
        ...ResultsTable.propTypes!,
        competitionPhase: isPropTypeNullable(PropTypes.string),
        competitionPhaseRound: isPropTypeNullable(PropTypes.string),
        round: PropTypes.number.isRequired,
      }).isRequired
    ).isRequired,
    competitionMatchListMenu: PropTypes.shape({
      activeIndex: PropTypes.number.isRequired,
      gliderItems: PropTypes.arrayOf(
        PropTypes.shape({
          callback: isPropTypeNullable(PropTypes.func),
          href: isPropTypeNullable(PropTypes.string),
          id: isPropTypeNullable(PropTypes.number),
          itemState: isPropTypeNullable(
            PropTypes.oneOf([GLIDER_ITEM_STATE_ENABLED, GLIDER_ITEM_STATE_DISABLED, GLIDER_ITEM_STATE_FOCUSED])
          ),
          label: PropTypes.node.isRequired,
        }).isRequired
      ).isRequired,
    }),
    trackingData: PropTypes.object,
  };
}

export default CompetitionCalendarResults;
